import { Button, Modal } from '@birdi/components-common';
import { next } from '@birdi/theme';
import { css } from '@emotion/react';
import { useState } from 'react';
import { stroot, maps as mapsApi } from '@birdi/js-sdk';
import WorkspaceSelect from '@birdi/select/WorkspaceSelect';
import toast from 'react-hot-toast';

interface MoveToWorkspaceFormProps {
  currentWorkspaceId: string | null;
  onSuccess: () => void;
  mapId: string;
}

const MoveToWorkspaceForm = ({
  currentWorkspaceId,
  onSuccess,
  mapId,
}: MoveToWorkspaceFormProps) => {
  const [selectedWorkspace, setSelectedWorkspace] = useState<{
    value: string;
    label: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async () => {
    setError(null);

    if (!selectedWorkspace) {
      setError('Please select a workspace');
      return;
    }

    if (selectedWorkspace.value === currentWorkspaceId) {
      setError('Map is already in this workspace');
      return;
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading('Moving map to workspace...');

    try {
      await mapsApi.updateMap(stroot('mvwrk1'), mapId, {
        organisationId: selectedWorkspace.value,
        folderId: null, // Clear folder when moving to different workspace
      } as any);
      toast.success(`Map moved to ${selectedWorkspace.label}`);
      onSuccess();
    } catch (err) {
      console.error('Error moving map to workspace:', err);
      toast.error('Failed to move map to workspace');
    } finally {
      toast.dismiss(loadingToast);
      setIsSubmitting(false);
    }
  };

  return (
    <form
      id="move-to-workspace-form"
      css={next.form.root}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <label css={next.form.field.root}>
        <span
          css={(theme) => css`
            color: ${theme.midnight};
          `}
        >
          Select the workspace you want to move this map to:
        </span>
        <div
          css={css`
            margin-top: 0.5rem;
          `}
        >
          <WorkspaceSelect
            onChange={(option: { value: string; label: string } | null) => {
              setSelectedWorkspace(option);
              setError(null); // Clear error when selection changes
            }}
            placeholder="Select workspace"
            isClearable
            disabledOptions={currentWorkspaceId ? [currentWorkspaceId] : []}
            containerStyles={{ width: '100%' }}
          />
        </div>
        <div
          css={css`
            height: 50px;
            display: flex;
            align-items: center;
          `}
        >
          {error && (
            <span
              css={css`
                color: red;
                font-size: 0.875rem;
                font-family: Proxima Nova;
              `}
            >
              {error}
            </span>
          )}
        </div>
      </label>
    </form>
  );
};

export interface MoveToWorkspaceModalProps {
  open: boolean;
  onChange: (value: boolean) => void;
  currentWorkspaceId: string | null;
  mapId: string;
}

export const MoveToWorkspaceModal = ({
  open,
  onChange,
  currentWorkspaceId,
  mapId,
}: MoveToWorkspaceModalProps) => {
  return (
    <Modal.Root open={open} onOpenChange={onChange}>
      <Modal.Header>Move map to workspace</Modal.Header>

      <Modal.Body>
        <div
          css={css`
            overflow: visible;
            position: relative;
            z-index: 1;
          `}
        >
          <MoveToWorkspaceForm
            currentWorkspaceId={currentWorkspaceId}
            onSuccess={() => {
              // Capture URL before closing modal to prevent race condition
              const currentUrl = window.location.href;
              onChange(false);
              // Force reload to the captured URL
              window.location.href = currentUrl;
            }}
            mapId={mapId}
          />
        </div>
      </Modal.Body>

      <Modal.Footer buttons>
        <Button variant="outline" onClick={() => onChange(false)}>
          Cancel
        </Button>
        <Button variant="solid" type="submit" form="move-to-workspace-form">
          Move
        </Button>
      </Modal.Footer>
    </Modal.Root>
  );
};

